import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Shield, Copy, Check, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import type { TotpSetupResponse } from '@uniticket/shared';

type Step = 'qr' | 'verify' | 'recovery';

export function TotpSetup() {
  const navigate = useNavigate();
  const { state, totpSetup, totpConfirm } = useAuth();

  const { t } = useTranslation('auth');
  const [step, setStep] = useState<Step>('qr');
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setRef = useCallback((index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  }, []);

  // Fetch QR code on mount
  useEffect(() => {
    if (state.status !== 'totp_setup') return;

    let cancelled = false;
    totpSetup().then((data) => {
      if (!cancelled) setSetupData(data);
    }).catch((err) => {
      if (!cancelled) setError(err.message);
    });
    return () => { cancelled = true; };
  }, [state.status, totpSetup]);

  // Redirect if not in TOTP setup state
  if (state.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }
  if (state.status !== 'totp_setup') {
    return <Navigate to="/login" replace />;
  }

  function handleDigitChange(index: number, value: string) {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 1) {
      // Multi-character = paste fallback
      const newDigits = [...digits];
      cleaned.slice(0, 6).split('').forEach((char, i) => {
        if (i < 6) newDigits[i] = char;
      });
      setDigits(newDigits);
      inputRefs.current[Math.min(cleaned.length, 5)]?.focus();
      return;
    }
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = [...digits];
    paste.split('').forEach((char, i) => {
      if (i < 6) newDigits[i] = char;
    });
    setDigits(newDigits);
    const nextIndex = Math.min(paste.length, 5);
    inputRefs.current[nextIndex]?.focus();
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6 || !setupData) return;

    setError('');
    setIsLoading(true);
    try {
      const codes = await totpConfirm(code, setupData.secret);
      setRecoveryCodes(codes);
      setStep('recovery');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyAll() {
    await navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full max-w-md">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 space-y-6">
          {/* Shield icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* ── Step 1: QR Code ─────────────────────────────── */}
          {step === 'qr' && (
            <>
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">{t('totpSetup.title')}</h2>
                <p className="text-white/60 mt-2 text-sm leading-relaxed">
                  {t('totpSetup.step2Desc')}
                </p>
              </div>

              {setupData ? (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="bg-white rounded-xl p-3">
                      <img src={setupData.qrCodeDataUrl} alt="TOTP QR Code" className="w-48 h-48" />
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-xs text-white/40 mb-1">{t('totpSetup.manualEntry')}</p>
                    <code className="text-sm font-mono text-white/80 bg-white/10 px-3 py-1.5 rounded-lg border border-white/20 select-all">
                      {setupData.secret}
                    </code>
                  </div>

                  <button
                    type="button"
                    onClick={() => setStep('verify')}
                    className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    {t('totpSetup.next', { defaultValue: 'Next' })}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : error ? (
                <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm">
                  {error}
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
            </>
          )}

          {/* ── Step 2: Verify Code ────────────────────────── */}
          {step === 'verify' && (
            <>
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">{t('totpSetup.step3', { defaultValue: 'Verify Your Authenticator' })}</h2>
                <p className="text-white/60 mt-2 text-sm leading-relaxed">
                  {t('totpSetup.step3Desc')}
                </p>
              </div>

              <form onSubmit={handleVerify} className="space-y-6">
                {error && (
                  <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex justify-center gap-2 sm:gap-3">
                  {digits.map((digit, i) => (
                    <span key={i} className="contents">
                      {i === 3 && (
                        <div className="flex items-center text-white/30 text-xl font-light">&ndash;</div>
                      )}
                      <input
                        ref={setRef(i)}
                        type="text"
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        onPaste={handlePaste}
                        placeholder="·"
                        className="glass-input w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold rounded-lg text-white placeholder:text-white/25 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 caret-white"
                      />
                    </span>
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={digits.join('').length < 6 || isLoading}
                  className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {isLoading ? t('totpSetup.confirming') : t('totpSetup.confirm')}
                </button>
              </form>

              <button
                type="button"
                onClick={() => { setStep('qr'); setError(''); setDigits(['', '', '', '', '', '']); }}
                className="w-full text-center text-sm text-white/50 hover:text-white/80 transition-colors"
              >
                {t('totpSetup.backToQr', { defaultValue: 'Back to QR code' })}
              </button>
            </>
          )}

          {/* ── Step 3: Recovery Codes ─────────────────────── */}
          {step === 'recovery' && (
            <>
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">{t('totpSetup.recoveryCodes')}</h2>
                <p className="text-white/60 mt-2 text-sm leading-relaxed">
                  {t('totpSetup.recoveryCodesDesc')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, i) => (
                  <div
                    key={i}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-center font-mono text-sm text-white/90 select-all"
                  >
                    {code}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleCopyAll}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/80 bg-white/10 border border-white/20 hover:bg-white/15 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t('totpSetup.copied') : t('totpSetup.copyAll')}
              </button>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="ack-codes"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="appearance-none w-4 h-4 mt-0.5 border border-white/35 rounded bg-white/[0.12] cursor-pointer checked:bg-gradient-to-br checked:from-brand-rose checked:to-brand-orange checked:border-transparent focus:outline-none focus:ring-[3px] focus:ring-brand-rose/20 relative shrink-0
                    after:content-['✓'] after:absolute after:text-white after:text-[11px] after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                />
                <label htmlFor="ack-codes" className="text-sm text-white/70 leading-snug">
                  {t('totpSetup.ackCodes', { defaultValue: 'I have saved my recovery codes in a safe place' })}
                </label>
              </div>

              <button
                type="button"
                disabled={!acknowledged}
                onClick={() => navigate('/dashboard')}
                className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {t('totpSetup.continue')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">&copy; 2026 Unifiber Belgium</p>
      </div>
    </div>
  );
}
